import React from 'react';
import {useTranslation} from 'react-i18next';

import isEmpty from 'lodash/isEmpty';

import ExpandLessIcon from '@edna/icons/expandLess.svg';
import ExpandMoreIcon from '@edna/icons/expandMore.svg';

import {Tab} from 'src/components';
import {NoShrinkImage} from 'src/components/primitives';
import {TMessageMatcher} from 'src/containers/MessageMatchers/definitions';
import {EPushAppType} from 'src/containers/PushApps/definitions';
import {useToggle} from 'src/hooks';
import {AndroidCircleIcon, IOSCircleIcon} from 'src/icons';
import {AndroidImage, IOSImage} from 'src/images';
import {lengthUnicode, sliceUnicode} from 'src/utils/unicode';

import * as S from './style';

const BUTTON_TRUNCATE_LENGTH = 15;

type TProps = {
  content: TMessageMatcher['content'];
  children: React.ReactNode;
};

const APP_TITLE = 'App Name';

const devices = [
  {type: EPushAppType.ANDROID, label: 'Android', icon: AndroidCircleIcon},
  {type: EPushAppType.IOS, label: 'iOS', icon: IOSCircleIcon},
];

export const PushContent = React.memo<TProps>(({content, children}) => {
  const {t} = useTranslation();
  const [device, setDevice] = React.useState(EPushAppType.ANDROID);
  const [isCollapsed, toggleCollapse] = useToggle(true);

  const isIOS = device === EPushAppType.IOS;
  const headerText = content.header?.text;

  const attachmentUrl = content.bigContent?.header?.attachment?.fileUrl;
  const headerAttachmentUrl = content.header?.attachment?.fileUrl;

  const renderActions = () => {
    if (content.buttons?.length && isCollapsed) {
      return (
        <S.Actions isIOS={isIOS}>
          {content.buttons
            .filter((button) => button.text)
            .map((button) => (
              <S.Action key={button.text} isIOS={isIOS}>
                {sliceUnicode(button.text!, 0, BUTTON_TRUNCATE_LENGTH)}
                {lengthUnicode(button.text!) > BUTTON_TRUNCATE_LENGTH && '...'}
              </S.Action>
            ))}
        </S.Actions>
      );
    }

    return null;
  };

  const renderCollapsedContent = () => (
    <S.Container>
      {!attachmentUrl && headerAttachmentUrl && <S.Logo isIOS={isIOS} src={headerAttachmentUrl} />}
      <div>
        {headerText && <S.BigTitle isIOS={isIOS}>{headerText}</S.BigTitle>}
        {children && (
          <S.BigBody isIOS={isIOS} hasImage={attachmentUrl !== undefined}>
            {children}
          </S.BigBody>
        )}
      </div>
    </S.Container>
  );

  const renderContent = () => {
    if (isCollapsed) {
      return (
        <div>
          {!isIOS && renderCollapsedContent()}
          {attachmentUrl && (
            <S.BigLogoWrapper isIOS={isIOS}>
              <S.BigLogo isIOS={isIOS} src={attachmentUrl} />
            </S.BigLogoWrapper>
          )}
          {isIOS && renderCollapsedContent()}
        </div>
      );
    }

    return (
      <S.Container>
        {headerAttachmentUrl && <S.Logo src={headerAttachmentUrl} />}
        <div>
          {headerText && <S.Title>{headerText}</S.Title>}
          {children && (
            <S.Body isIOS={isIOS} hasTitle={!isEmpty(headerText)}>
              {children}
            </S.Body>
          )}
        </div>
      </S.Container>
    );
  };

  const getHeaderIcon = () => {
    if (isIOS) {
      if (isCollapsed) {
        return <S.MoreHorizIcon />;
      }

      return <S.Time>3m ago</S.Time>;
    }

    if (isCollapsed) {
      return <NoShrinkImage as={ExpandLessIcon} />;
    }

    return <NoShrinkImage as={ExpandMoreIcon} />;
  };

  return (
    <>
      <S.Tabs>
        {devices.map(({type: deviceType, label, icon}) => {
          const active = deviceType === device;

          return (
            <Tab key={deviceType} active={active} onClick={() => setDevice(deviceType)}>
              <S.Device>
                <S.DeviceIcon as={icon} />
                {label}
              </S.Device>
            </Tab>
          );
        })}
      </S.Tabs>
      <S.Switch
        checked={isCollapsed}
        label={<S.SwitchContent>{t('MessagePreview:expandedPush')}</S.SwitchContent>}
        onChange={toggleCollapse}
      />
      <S.PhonePreview>
        <S.PreviewBackground as={isIOS ? IOSImage : AndroidImage} />
        <S.Content isIOS={isIOS}>
          <S.MainBody>
            <S.Message collapsed={isCollapsed} isIOS={isIOS}>
              <S.Header>
                <S.AppTitle>
                  <S.AppLogo />
                  <S.AppTitleText>{APP_TITLE}</S.AppTitleText>
                </S.AppTitle>
                {getHeaderIcon()}
              </S.Header>
              {renderContent()}
              {!isIOS && renderActions()}
            </S.Message>
            {isIOS && renderActions()}
          </S.MainBody>
        </S.Content>
      </S.PhonePreview>
    </>
  );
});

PushContent.displayName = 'PushContent';
